import { useState } from 'react'
import { api, ApiError, session } from '../lib/api'
import { FormDrawer, type FormFieldDef } from '../ui/FormDrawer'
import { useEnumOptions } from '../lib/enums'
import { useApi } from '../lib/hooks'
import { against, money, num, shortDate, titleCase } from '../lib/format'
import { Banner, Empty, ErrorNote, Kpi, Loading, Panel, Pill, StatMini } from '../ui/kit'

interface Ageing {
  buckets: Array<{ bucket: string; outstanding: number; invoices: number; sharePct: number | null; topDebtor: string | null; maxDays: number }>
  totalReceivable: number
  concentration: Array<{ buyerId: string; buyerName: string; exposure: number; sharePct: number; capPct: number; overCap: boolean }>
  cashCycle: { daysToPayFarmer: number; inventoryDays: number; dsoDays: number; cashCycleDays: number; targetDsoDays: number } | null
  inflowNext7Days: number
  payablesToday: number
}

interface UnitEconomics {
  from: string; to: string
  deliveredKg: number; revenue: number; purchasedKg: number
  farmgateCost: number; shrinkCost: number; transportCost: number; cessCost: number
  depotCost: number; packagingCost: number; irrecoverableVat: number
  financingCost: number; otherCost: number; rejectionLoss: number
  contribution: number
  revenuePerKg: number | null; farmgateCostPerKg: number | null; contributionPerKg: number | null
  shrinkPct: number | null; shrinkTargetPct: number
}

interface Uninvoiced {
  id: string; code: string; status: string
  completedAt: string | null; signedBy: string | null
  acceptedKg: number; rejectedKg: number
  buyerId: string; buyerName: string; kraPin: string | null
  termsDays: number; creditState: string
  orderCode: string; value: number
}

interface Buyer { id: string; name: string }

interface Invoice {
  id: string; number: string; issueDate: string; dueDate: string
  total: number; amountPaid: number; balance: number; status: string
  etimsStatus: string; etimsInvoiceNo: string | null
  buyerName: string; daysOverdue: number; daysOutstanding: number
}

const BUCKET_TONE: Record<string, 'ok' | 'warn' | 'crit'> = {
  '0-30': 'ok', '31-60': 'warn', '61-90': 'warn', '90+': 'crit',
}

export function Finance() {
  const canWrite = session.has('admin', 'finance')

  const ageing = useApi<Ageing>('/finance/ageing')
  const econ = useApi<UnitEconomics>('/finance/unit-economics')
  const invoices = useApi<Invoice[]>('/finance/invoices')
  const uninvoiced = useApi<Uninvoiced[]>('/finance/uninvoiced')

  const [raising, setRaising] = useState<Uninvoiced | null>(null)
  const [receipting, setReceipting] = useState(false)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)

  function reload() {
    invoices.reload()
    uninvoiced.reload()
    ageing.reload()
  }

  async function submitEtims(inv: Invoice) {
    const question =
      'Send ' + inv.number + ' to eTIMS?\n\n' +
      'This is what makes it a compliant tax invoice, and it is the reason a formal ' +
      'buyer deals with you rather than the open market.'
    if (!window.confirm(question)) return
    try {
      await api.post(`/finance/invoices/${inv.id}/etims`)
      setBanner({ tone: 'ok', text: `${inv.number} submitted. KRA confirms the control code back.` })
      reload()
    } catch (e) {
      setBanner({ tone: 'crit', text: e instanceof ApiError ? e.message : 'That did not work.' })
    }
  }

  const a = ageing.data
  const e = econ.data

  return (
    <main className="page">
      <div className="page-head">
        <h1>Invoices</h1>
        <p>
          Who owes you, how long they have owed it, and how long your own money is tied up between
          paying a farmer and being paid by a buyer. Running out of cash will close you faster than
          a thin margin will.
        </p>
      </div>

      {ageing.error && <ErrorNote error={ageing.error} onRetry={ageing.reload} />}

      <div className="stack">
        {banner && (
          <Banner tone={banner.tone === 'ok' ? 'info' : 'crit'} title={banner.text}>{null}</Banner>
        )}

        {a && (
          <div className="kpi-grid">
            <Kpi
              label="Buyers owe you"
              value={money(a.totalReceivable, { compact: true })}
              sub={a.cashCycle
                ? <>taking {a.cashCycle.dsoDays} days to collect — aim for {a.cashCycle.targetDsoDays}</>
                : null}
              tone={a.cashCycle ? against(a.cashCycle.dsoDays, a.cashCycle.targetDsoDays, false) : 'neutral'}
              meterPct={a.cashCycle ? (a.cashCycle.dsoDays / Math.max(a.cashCycle.targetDsoDays, 1)) * 100 : null}
            />
            <Kpi
              label="You owe farmers"
              value={money(a.payablesToday, { compact: true })}
              sub="waiting to be sent today"
            />
            <Kpi
              label="Your money is tied up for"
              value={a.cashCycle?.cashCycleDays ?? '—'}
              unit="days"
              sub={
                a.cashCycle
                  ? <>
                      {a.cashCycle.daysToPayFarmer} d to pay the farmer,
                      {' '}{a.cashCycle.inventoryDays} d in the store,
                      {' '}{a.cashCycle.dsoDays} d waiting for the buyer
                    </>
                  : null
              }
              tone={a.cashCycle ? against(a.cashCycle.cashCycleDays, 40, false) : 'neutral'}
            />
            <Kpi
              label="Coming in this week"
              value={money(a.inflowNext7Days, { compact: true })}
              sub="invoices due in the next 7 days"
            />
            <Kpi
              label="Profit per kilo"
              value={e?.contributionPerKg === null || e === null ? '—' : Number(e.contributionPerKg).toFixed(2)}
              unit="KES"
              sub={e ? <>across {num(e.deliveredKg)} kg delivered</> : null}
              tone={e && Number(e.contributionPerKg ?? 0) > 0 ? 'ok' : 'crit'}
            />
            <Kpi
              label="Weight lost"
              value={e?.shrinkPct ?? '—'}
              unit="%"
              sub={e ? <>keep under {e.shrinkTargetPct}%</> : null}
              tone={e ? against(e.shrinkPct, e.shrinkTargetPct, false) : 'neutral'}
            />
          </div>
        )}

        <div className="split">
          <div className="stack">
            <Panel
              title="Ready to invoice"
              meta={
                canWrite && (
                  <button className="btn quiet" onClick={() => setReceipting(true)}>Record a receipt</button>
                )
              }
            >
              {uninvoiced.error && <ErrorNote error={uninvoiced.error} onRetry={uninvoiced.reload} />}
              {uninvoiced.loading && <Loading rows={2} />}
              {uninvoiced.data?.length === 0 && (
                <Empty title="Everything delivered has been invoiced">
                  Produce that left the van and never got billed is the easiest money to lose. Keep
                  this list empty.
                </Empty>
              )}
              {uninvoiced.data && uninvoiced.data.length > 0 && (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Delivery</th><th>Buyer</th><th>Order</th>
                        <th className="n">Accepted</th><th className="n">Value</th>
                        <th>Signed by</th><th className="n">Delivered</th>
                        {canWrite && <th style={{ width: 90 }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {uninvoiced.data.map((d) => (
                        <tr key={d.id} className="attention">
                          <td className="code strong">{d.code}</td>
                          <td className="truncate">
                            {d.buyerName}
                            {!d.kraPin && <> <Pill tone="warn" dot={false}>no KRA PIN</Pill></>}
                          </td>
                          <td className="code faint">{d.orderCode}</td>
                          <td className="n">
                            {num(d.acceptedKg, 1)} kg
                            {Number(d.rejectedKg) > 0 && (
                              <span className="crit-t"> −{num(d.rejectedKg, 1)}</span>
                            )}
                          </td>
                          <td className="n strong">{money(d.value, { currency: false })}</td>
                          <td className="truncate faint">{d.signedBy ?? '\u2014'}</td>
                          <td className="n faint">{d.completedAt ? shortDate(d.completedAt) : '\u2014'}</td>
                          {canWrite && (
                            <td style={{ textAlign: 'right' }}>
                              <button className="btn" style={tinyBtn} onClick={() => setRaising(d)}>
                                Invoice
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            <Panel
              title="How long buyers have owed you"
              meta="check this weekly — buyers quietly start paying later"
            >
              {ageing.loading && <Loading rows={4} />}
              {a && a.buckets.length === 0 && (
                <Empty title="Nobody owes you anything">
                  Every invoice has been paid.
                </Empty>
              )}
              {a && a.buckets.length > 0 && (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Bucket</th><th className="n">Outstanding</th><th className="n">Share</th>
                        <th>Owes the most</th><th className="n">Oldest</th><th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.buckets.map((b) => (
                        <tr key={b.bucket} className={b.bucket === '90+' ? 'attention' : ''}>
                          <td className="strong">{b.bucket} days</td>
                          <td className="n">{money(b.outstanding, { currency: false })}</td>
                          <td className="n">{b.sharePct ?? 0}%</td>
                          <td className="truncate">{b.topDebtor ?? '—'}</td>
                          <td className="n">{b.maxDays} d</td>
                          <td>
                            <Pill tone={BUCKET_TONE[b.bucket] ?? 'neutral'}>
                              {b.bucket === '0-30' ? 'On track'
                                : b.bucket === '31-60' ? 'Call scheduled'
                                : b.bucket === '61-90' ? 'Hold deliveries'
                                : 'Escalate'}
                            </Pill>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            {e && <UnitEconomicsWaterfall e={e} />}

            <Panel title="Invoices raised" meta="fresh produce carries no VAT, so there is no tax to add">
              {invoices.loading && <Loading rows={4} />}
              {invoices.data?.length === 0 && (
                <Empty title="No invoices yet">
                  You raise an invoice from a delivery that has been signed for, so what you bill is
                  always what the buyer actually took.
                </Empty>
              )}
              {invoices.data && invoices.data.length > 0 && (
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Number</th><th>Buyer</th><th className="n">Total</th>
                        <th className="n">Balance</th><th className="n">Days</th><th>Status</th><th>Sent to KRA</th>
                        {canWrite && <th style={{ width: 90 }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.data.map((i) => (
                        <tr key={i.id} className={i.daysOverdue > 0 ? 'attention' : ''}>
                          <td className="code strong">{i.number}</td>
                          <td className="truncate">{i.buyerName}</td>
                          <td className="n">{money(i.total, { currency: false })}</td>
                          <td className="n">{money(i.balance, { currency: false })}</td>
                          <td className="n">{i.daysOutstanding} d</td>
                          <td><Pill tone={i.status === 'paid' ? 'ok' : i.daysOverdue > 0 ? 'crit' : 'info'}>
                            {titleCase(i.status)}
                          </Pill></td>
                          <td>
                            <Pill tone={i.etimsStatus === 'accepted' ? 'ok' : i.etimsStatus === 'rejected' ? 'crit' : 'warn'}>
                              {titleCase(i.etimsStatus)}
                            </Pill>
                            {i.etimsInvoiceNo && (
                              <div className="mono faint" style={{ fontSize: 'var(--fs-2xs)' }}>
                                {i.etimsInvoiceNo}
                              </div>
                            )}
                          </td>
                          {canWrite && (
                            <td style={{ textAlign: 'right' }}>
                              {['pending', 'rejected', 'failed'].includes(i.etimsStatus) && (
                                <button className="btn quiet" style={tinyBtn}
                                  onClick={() => void submitEtims(i)}>
                                  {i.etimsStatus === 'pending' ? 'Send to eTIMS' : 'Resend'}
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>

          <div className="stack">
            <Panel title="How much rides on each buyer" meta="if your biggest buyer paid a month late, could you carry it?">
              {a?.concentration.length === 0 && (
                <div className="panel-body"><p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
                  No open exposure.
                </p></div>
              )}
              {a && a.concentration.length > 0 && (
                <div className="panel-body col" style={{ gap: 10 }}>
                  {a.concentration.map((c) => (
                    <div key={c.buyerId}>
                      <div className="spread" style={{ marginBottom: 4 }}>
                        <span className="truncate" style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>
                          {c.buyerName}
                        </span>
                        <span className={`num ${c.overCap ? 'crit-t' : ''}`} style={{ fontSize: 'var(--fs-sm)', fontWeight: 700 }}>
                          {c.sharePct}%
                        </span>
                      </div>
                      <div className={`meter ${c.overCap ? 'is-crit' : ''}`}>
                        <i style={{ width: `${Math.min(100, c.sharePct)}%` }} />
                      </div>
                      <div className="hint" style={{ marginTop: 3 }}>
                        {money(c.exposure, { compact: true })} · your limit is {c.capPct}%
                        {c.overCap && <span className="crit-t"> — too much rides on them, find another buyer</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            {a?.cashCycle && (
              <Panel title="Where your money sits">
                <div className="panel-body col" style={{ gap: 10 }}>
                  <CycleStep label="You pay the farmer" days={a.cashCycle.daysToPayFarmer} tone="ok" />
                  <CycleStep label="Produce sits in the store" days={a.cashCycle.inventoryDays} tone="ok" />
                  <CycleStep
                    label="You wait for the buyer"
                    days={a.cashCycle.dsoDays}
                    tone={a.cashCycle.dsoDays > a.cashCycle.targetDsoDays ? 'crit' : 'ok'}
                  />
                  <div className="divider" />
                  <div className="spread">
                    <b style={{ fontSize: 'var(--fs-sm)' }}>Cash cycle</b>
                    <b className="num" style={{ fontSize: 'var(--fs-lg)' }}>{a.cashCycle.cashCycleDays} days</b>
                  </div>
                  <p className="hint">
                    Growth consumes cash faster than this business generates it, at every level of
                    scale. Set the maximum monthly growth the balance sheet can fund and hold to it.
                  </p>
                </div>
              </Panel>
            )}
          </div>
        </div>
      </div>

      {raising && (
        <RaiseInvoice
          delivery={raising}
          onClose={() => setRaising(null)}
          onDone={(number) => {
            setRaising(null)
            setBanner({ tone: 'ok', text: `${number} raised. Send it to eTIMS to make it a tax invoice.` })
            reload()
          }}
        />
      )}

      {receipting && (
        <RecordReceipt
          onClose={() => setReceipting(false)}
          onDone={(amount) => {
            setReceipting(false)
            setBanner({ tone: 'ok', text: `${money(amount)} receipted and applied.` })
            reload()
          }}
        />
      )}
    </main>
  )
}

// ---------------------------------------------------------------- raise invoice
function RaiseInvoice({ delivery, onClose, onDone }: {
  delivery: Uninvoiced
  onClose: () => void
  onDone: (number: string) => void
}) {
  const [values, setValues] = useState<Record<string, unknown>>({
    termsDays: String(delivery.termsDays ?? 0),
  })
  const set = (name: string, v: unknown) => setValues((s) => ({ ...s, [name]: v }))

  const fields: FormFieldDef[] = [
    {
      name: 'termsDays', label: 'Payment terms', type: 'number', required: true, half: true,
      suffix: 'days', hint: "The buyer's agreed terms, unless this delivery was agreed differently",
    },
    {
      name: 'notes', label: 'Notes on the invoice', type: 'textarea',
      hint: 'The buyer reads this. Name the delivery and what was short, if anything.',
    },
  ]

  async function submit() {
    const res = await api.post<{ number: string }>('/finance/invoices/from-delivery', {
      deliveryId: delivery.id,
      termsDays: values.termsDays ? Number(values.termsDays) : null,
      notes: values.notes || null,
    })
    onDone(res.number)
  }

  const due = new Date(Date.now() + (Number(values.termsDays) || 0) * 86400000)

  return (
    <FormDrawer
      kind="Invoice" title={`${delivery.buyerName} \u00b7 ${delivery.code}`}
      submitLabel="Raise invoice"
      fields={fields} values={values} onChange={set}
      onSubmit={submit} onClose={onClose}
      footer={<>due <b>{shortDate(due.toISOString())}</b></>}
    >
      <div className="panel-body" style={{ padding: 0 }}>
        <div className="spread" style={{ fontSize: 'var(--fs-sm)', padding: '3px 0' }}>
          <span className="muted">Accepted at the door</span>
          <b className="num">{num(delivery.acceptedKg, 1)} kg</b>
        </div>
        {Number(delivery.rejectedKg) > 0 && (
          <div className="spread" style={{ fontSize: 'var(--fs-sm)', padding: '3px 0' }}>
            <span className="muted">Rejected, and not billed</span>
            <b className="num crit-t">{num(delivery.rejectedKg, 1)} kg</b>
          </div>
        )}
        <div className="spread" style={{ fontSize: 'var(--fs-sm)', padding: '3px 0' }}>
          <span className="muted">Value</span>
          <b className="num">{money(delivery.value)}</b>
        </div>
      </div>

      {!delivery.kraPin && (
        <Banner tone="warn" title="This buyer has no KRA PIN saved">
          You can still raise the invoice, but it cannot be sent to KRA without one — and a proper
          tax invoice is most of why a formal buyer deals with you instead of the open market. Add
          the PIN on the buyer's record.
        </Banner>
      )}

      {delivery.creditState === 'on_hold' && (
        <Banner tone="crit" title="This buyer is on hold">
          Raise the invoice anyway so the debt is on record — but send them nothing more until the
          hold is lifted.
        </Banner>
      )}

      <p className="hint" style={{ marginTop: 'var(--sp-6)' }}>
        The amounts come from what the buyer signed for, not from what was loaded. Anything they
        sent back is not on the invoice.
      </p>
    </FormDrawer>
  )
}

// ---------------------------------------------------------------- receipt
function RecordReceipt({ onClose, onDone }: {
  onClose: () => void
  onDone: (amount: number) => void
}) {
  const buyers = useApi<Buyer[]>('/buyers?pageSize=200')
  const open = useApi<Invoice[]>('/finance/invoices?status=issued')
  const channels = useEnumOptions('receipt_channel', {
    mpesa: 'M-Pesa', bank: 'Bank transfer', offset: 'Offset against a credit note',
  })

  const [values, setValues] = useState<Record<string, unknown>>({ channel: 'mpesa' })
  const [applied, setApplied] = useState<Record<string, string>>({})
  const set = (name: string, v: unknown) => setValues((s) => ({ ...s, [name]: v }))

  const mine = (open.data ?? []).filter(
    (i) => !values.buyerId || i.buyerName === (buyers.data ?? []).find((b) => b.id === values.buyerId)?.name,
  )

  const fields: FormFieldDef[] = [
    {
      name: 'buyerId', label: 'Who paid', type: 'select', required: true,
      options: (buyers.data ?? []).map((b) => ({ value: b.id, label: b.name })),
    },
    { name: 'channel', label: 'How', type: 'select', required: true, half: true, options: channels },
    { name: 'amount', label: 'Amount', type: 'money', required: true, half: true },
    {
      name: 'providerRef', label: 'Reference', half: true,
      hint: 'The M-Pesa code or bank reference, so this can be traced later',
    },
    { name: 'narrative', label: 'Narrative', half: true },
  ]

  const total = Number(values.amount) || 0
  const allocated = Object.values(applied).reduce((s, v) => s + (Number(v) || 0), 0)
  const unapplied = total - allocated

  async function submit() {
    if (total <= 0) throw new Error('Enter what they actually paid.')
    if (allocated > total) throw new Error('You have applied more than the receipt is worth.')

    const allocations = Object.entries(applied)
      .filter(([, v]) => Number(v) > 0)
      .map(([invoiceId, v]) => ({ invoiceId, amount: Number(v) }))

    await api.post('/finance/receipts', {
      buyerId: values.buyerId,
      channel: values.channel,
      amount: total,
      providerRef: values.providerRef || null,
      narrative: values.narrative || null,
      allocations: allocations.length > 0 ? allocations : null,
    })
    onDone(total)
  }

  return (
    <FormDrawer
      wide kind="Payment received" title="Money from a buyer" submitLabel="Record it"
      fields={fields} values={values} onChange={set}
      onSubmit={submit} onClose={onClose}
      footer={
        <>
          {money(allocated)} applied
          {unapplied !== 0 && (
            <span className={unapplied < 0 ? 'crit-t' : 'warn-t'}>
              {' \u00b7 '}{money(Math.abs(unapplied))} {unapplied < 0 ? 'over' : 'on account'}
            </span>
          )}
        </>
      }
    >
      <div className="micro" style={{ marginTop: 'var(--sp-7)', marginBottom: 'var(--sp-3)' }}>
        Apply it to
      </div>

      {open.loading && <Loading rows={3} />}
      {mine.length === 0 && !open.loading && (
        <p className="hint">
          Nothing open for them. Record it anyway and it sits on account against the next invoice.
        </p>
      )}

      {mine.map((i) => (
        <div className="line-row" key={i.id}>
          <div className="grow">
            <b className="code">{i.number}</b>
            <div className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
              {i.buyerName} · {money(i.balance, { currency: false })} outstanding
              {i.daysOverdue > 0 && <span className="crit-t"> · {i.daysOverdue} d overdue</span>}
            </div>
          </div>
          <div className="narrow">
            <input className="input n" type="number" step="0.01" min="0" max={i.balance}
              placeholder="0"
              value={applied[i.id] ?? ''}
              onChange={(e) => setApplied((s) => ({ ...s, [i.id]: e.target.value }))} />
          </div>
        </div>
      ))}

      {mine.length > 0 && (
        <button type="button" className="line-add"
          onClick={() => {
            // Oldest first. Anything else is how a 90-day debt quietly becomes a bad one.
            // Invoices raised the same day tie on age, so fall back to the number, which
            // is issued in order - otherwise which one gets paid is whatever the list
            // happened to return.
            let left = total
            const next: Record<string, string> = {}
            const oldestFirst = [...mine].sort((x, y) =>
              y.daysOutstanding - x.daysOutstanding || x.number.localeCompare(y.number))
            for (const i of oldestFirst) {
              if (left <= 0) break
              const take = Math.min(left, Number(i.balance))
              next[i.id] = String(take)
              left -= take
            }
            setApplied(next)
          }}>
          Apply oldest first
        </button>
      )}

      {unapplied < 0 && (
        <Banner tone="crit" title="That is more than they paid">
          Applying {money(allocated)} out of a {money(total)} receipt does not balance.
        </Banner>
      )}
    </FormDrawer>
  )
}

const tinyBtn = { minHeight: 24, padding: '1px 8px', fontSize: 'var(--fs-2xs)' } as const

function CycleStep({ label, days, tone }: { label: string; days: number; tone: 'ok' | 'crit' }) {
  return (
    <div>
      <div className="spread" style={{ marginBottom: 4 }}>
        <span style={{ fontSize: 'var(--fs-sm)' }}>{label}</span>
        <span className={`num ${tone === 'crit' ? 'crit-t' : ''}`} style={{ fontWeight: 700, fontSize: 'var(--fs-sm)' }}>
          {days} d
        </span>
      </div>
      <div className={`meter ${tone === 'crit' ? 'is-crit' : ''}`}>
        <i style={{ width: `${Math.min(100, (days / 60) * 100)}%` }} />
      </div>
    </div>
  )
}

/**
 * The per-kilo waterfall. A 50%+ gross spread becomes a single-digit net margin, and
 * showing it as a stack is the only way the size of each bite is legible.
 */
function UnitEconomicsWaterfall({ e }: { e: UnitEconomics }) {
  const revenue = Number(e.revenue) || 0
  const rows: Array<{ label: string; amount: number }> = [
    { label: 'Farmgate purchase', amount: -Number(e.farmgateCost) },
    { label: 'Shrink & grade-out', amount: -Number(e.shrinkCost) },
    { label: 'Transport — haul and last mile', amount: -Number(e.transportCost) },
    { label: 'County cess and permits', amount: -Number(e.cessCost) },
    { label: 'Depot, labour and cold storage', amount: -Number(e.depotCost) },
    { label: 'Packaging and crates', amount: -Number(e.packagingCost) },
    { label: 'Irrecoverable input VAT', amount: -Number(e.irrecoverableVat) },
    { label: 'Financing', amount: -Number(e.financingCost) },
    { label: 'Rejections', amount: -Number(e.rejectionLoss) },
    { label: 'Other', amount: -Number(e.otherCost) },
  ].filter((r) => r.amount !== 0)

  const scale = Math.max(revenue, 1)
  const positive = Number(e.contribution) > 0

  return (
    <Panel
      title="What you make on a kilo"
      meta={`${e.from} to ${e.to} · ${num(e.deliveredKg)} kg delivered`}
      footer={
        <>
          <StatMini label="Sold for, per kg" value={e.revenuePerKg === null ? '—' : Number(e.revenuePerKg).toFixed(2)} />
          <StatMini label="Paid the farmer, per kg" value={e.farmgateCostPerKg === null ? '—' : Number(e.farmgateCostPerKg).toFixed(2)} />
          <StatMini
            label="Left over, per kg"
            value={e.contributionPerKg === null ? '—' : Number(e.contributionPerKg).toFixed(2)}
            note={positive ? 'positive' : 'loss-making'}
          />
          <StatMini label="Bought" value={`${num(e.purchasedKg)} kg`} />
        </>
      }
    >
      <div className="panel-body col" style={{ gap: 6 }}>
        <WaterfallRow label="Money in from buyers" amount={revenue} scale={scale} tone="ok" bold />
        {rows.map((r) => (
          <WaterfallRow key={r.label} label={r.label} amount={r.amount} scale={scale} tone="crit" />
        ))}
        <div className="divider" style={{ margin: '4px 0' }} />
        <WaterfallRow
          label="What is left"
          amount={Number(e.contribution)}
          scale={scale}
          tone={positive ? 'ok' : 'crit'}
          bold
        />
        {revenue === 0 && (
          <Banner tone="info" title="Nothing sold in this period yet">
            Deliver and invoice an order and the full waterfall fills in.
          </Banner>
        )}
      </div>
    </Panel>
  )
}

function WaterfallRow({
  label, amount, scale, tone, bold,
}: { label: string; amount: number; scale: number; tone: 'ok' | 'crit'; bold?: boolean }) {
  const width = Math.min(100, (Math.abs(amount) / scale) * 100)
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 110px 90px',
        gap: 12,
        alignItems: 'center',
        fontSize: 'var(--fs-sm)',
        fontWeight: bold ? 700 : 400,
      }}
    >
      <span className="truncate">{label}</span>
      <span className="num" style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
        {amount < 0 ? '−' : ''}{money(Math.abs(amount), { currency: false })}
      </span>
      <div className="meter" style={{ marginTop: 0 }}>
        <i style={{ width: `${width}%`, background: tone === 'ok' ? 'var(--brand-500)' : 'var(--crit)' }} />
      </div>
    </div>
  )
}
